import React from 'react';

import { Divider, Stack, TextField } from '@mui/material';
import { Label } from '@common/Label';
import { Select } from '@common/Select';
import { Option } from '@common/BucketInput/BucketField';
import { Expression } from '@common/Expression';
import { TelnyxAPISearchMetadata } from '../../../tool.types';

import {
  TTBackupOutput,
  TTMaxResults,
  TTMaxTokens,
  TTSearchTerm,
  TTSearchType,
} from '@common/Node/NodeSidebar/Extensions/BucketSearch';

import {
  TTBodyParameters,
  TTQueryParameters,
  TTResponse,
  TTSDKSamples,
  TTWebhooks,
} from '@common/Node/NodeSidebar/Extensions/APISearch';

interface Props {
  metadata: TelnyxAPISearchMetadata;
  setToolMetadata: React.Dispatch<React.SetStateAction<TelnyxAPISearchMetadata>>;
  toolArgumentOptions: object;
}

type textFieldParam =
  | 'search_descriptor'
  | 'bucket_search_type'
  | 'query_parameters'
  | 'body_parameters'
  | 'responses'
  | 'webhooks'
  | 'sdk'
  | 'backup_output';

export const TelnyxAPISearchExecution: React.FC<Props> = ({ metadata, setToolMetadata, toolArgumentOptions }) => {
  const updateTextField = (paramName: textFieldParam, value: string) => {
    setToolMetadata((prevState) => ({ ...prevState, [paramName]: value }));
  };

  const updateNumericField = (paramName: 'max_tokens' | 'max_results', value: number) => {
    setToolMetadata((prevState) => ({ ...prevState, [paramName]: value }));
  };

  const searchTypeOptions: Option[] = [
    { label: 'List & Search', value: 'list_and_search' },
    { label: 'Direct Search', value: 'direct_search' },
    { label: 'List', value: 'list' },
  ];

  return (
    <Stack spacing={2}>
      <Stack spacing={1}>
        <Label title='Search Term' tooltip={TTSearchTerm} />
        <Expression
          key={`search_descriptor`}
          keyProp={`search_descriptor`}
          value={metadata.search_descriptor}
          title={'Search Descriptor'}
          description={TTSearchTerm}
          setValue={(newValue: string) => updateTextField('search_descriptor', newValue)}
          options={toolArgumentOptions}
          placeholder='Search Term'
        />
      </Stack>
      <Stack spacing={1}>
        <Label title='Search Method' tooltip={TTSearchType} />
        <Select
          value={metadata.bucket_search_type}
          setValue={(value: string) => updateTextField('bucket_search_type', value)}
          options={searchTypeOptions}
          placeholder='Select an index search type'
        />
      </Stack>
      <Stack display='grid' gridTemplateColumns='1fr 1fr' gap='0.5rem'>
        <Stack spacing='8px'>
          <Label title='Max Tokens' tooltip={TTMaxTokens} />
          <TextField
            size='small'
            fullWidth
            placeholder='Max Tokens'
            value={metadata.max_tokens}
            type='number'
            onChange={(e) => {
              if (Number.parseInt(e.target.value) < 0) return;
              updateNumericField('max_tokens', Number.parseInt(e.target.value));
            }}
          />
        </Stack>
        <Stack spacing='8px'>
          <Label title='Max Results' tooltip={TTMaxResults} />
          <TextField
            size='small'
            fullWidth
            placeholder='Max Results'
            value={metadata.max_results}
            type='number'
            onChange={(e) => {
              if (Number.parseInt(e.target.value) < 1) return;
              updateNumericField('max_results', Number.parseInt(e.target.value));
            }}
          />
        </Stack>
      </Stack>
      <Stack spacing={1}>
        <Label title='Query Parameters' tooltip={TTQueryParameters} />
        <Expression
          key={`query_parameters`}
          keyProp={`query_parameters`}
          value={metadata.query_parameters}
          title={'Query Parameters'}
          description={TTQueryParameters}
          setValue={(newValue: string) => updateTextField('query_parameters', newValue)}
          options={toolArgumentOptions}
          placeholder='true | false'
        />
      </Stack>
      <Stack spacing={1}>
        <Label title='Body Parameters' tooltip={TTBodyParameters} />
        <Expression
          key={`body_parameters`}
          keyProp={`body_parameters`}
          value={metadata.body_parameters}
          title={'Body Parameters'}
          description={TTBodyParameters}
          setValue={(newValue: string) => updateTextField('body_parameters', newValue)}
          options={toolArgumentOptions}
          placeholder='true | false'
        />
      </Stack>
      <Stack spacing={1}>
        <Label title='Responses' tooltip={TTResponse} />
        <Expression
          key={`responses`}
          keyProp={`responses`}
          value={metadata.responses}
          title={'Responses'}
          description={TTResponse}
          setValue={(newValue: string) => updateTextField('responses', newValue)}
          options={toolArgumentOptions}
          placeholder='true | false'
        />
      </Stack>
      <Stack spacing={1}>
        <Label title='Webhooks' tooltip={TTWebhooks} />
        <Expression
          key={`webhooks`}
          keyProp={`webhooks`}
          value={metadata.webhooks}
          title={'Webhooks'}
          description={TTWebhooks}
          setValue={(newValue: string) => updateTextField('webhooks', newValue)}
          options={toolArgumentOptions}
          placeholder='true | false'
        />
      </Stack>
      <Stack spacing={1}>
        <Label title='SDK Code Sample' tooltip={TTSDKSamples} />
        <Expression
          key={`sdk`}
          keyProp={`sdk`}
          value={metadata.sdk}
          title={'SDK Code Samples'}
          description={TTSDKSamples}
          setValue={(newValue: string) => updateTextField('sdk', newValue)}
          options={toolArgumentOptions}
          placeholder='none | all | python | javascript | .net | ruby | php | java'
        />
      </Stack>
      <Divider />
      <Stack spacing={1}>
        <Label title='Backup Tool Output' tooltip={TTBackupOutput} />
        <TextField
          fullWidth
          size='small'
          value={metadata.backup_output}
          onChange={(e) => updateTextField('backup_output', e.target.value)}
          placeholder='There was no documentation found for the users question.'
        />
      </Stack>
    </Stack>
  );
};
